import React, { useState, useEffect } from 'react';
import Modal from '@material-ui/core/Modal';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { X } from 'tabler-icons-react';
import { useCalidad } from './CalidadContext';

const FORM_EMPTY = { defecto: '', causaRaiz: '', cantidad: 1, loteoSerie: '', notas: '' };

export default function CalidadFormModal() {
    const { formOpen, editTarget, guardando, errorGuardado, cerrarForm, guardarRegistro, catalogoDefectos, catalogoCausas } = useCalidad();

    const [values, setValues] = useState(FORM_EMPTY);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (formOpen) {
            setValues(
                editTarget
                    ? {
                          defecto: editTarget.defecto ?? '',
                          causaRaiz: editTarget.causaRaiz ?? '',
                          cantidad: editTarget.cantidad ?? 1,
                          loteoSerie: editTarget.loteoSerie || '',
                          notas: editTarget.notas || '',
                      }
                    : FORM_EMPTY
            );
            setErrors({});
        }
    }, [formOpen, editTarget]);

    const handleChange = (field) => (e) => {
        setValues((prev) => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const handleSubmit = () => {
        const newErrors = {};
        if (!values.defecto && values.defecto !== 0) newErrors.defecto = 'Selecciona un tipo de defecto';
        const cantidadNum = Number(values.cantidad);
        if (!values.cantidad || isNaN(cantidadNum) || cantidadNum < 1) {
            newErrors.cantidad = 'La cantidad debe ser al menos 1';
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        guardarRegistro({ ...values, cantidad: cantidadNum, loteoSerie: values.loteoSerie });
    };

    const titulo = editTarget ? 'Editar registro de calidad' : 'Registrar pieza de calidad';

    return (
        <Modal open={formOpen} onClose={cerrarForm}>
            <div className="usuarios-modal">
                <div className="usuarios-modal-header">
                    <h2 className="usuarios-modal-title">{titulo}</h2>
                    <IconButton size="small" onClick={cerrarForm}>
                        <X size={18} strokeWidth={2} />
                    </IconButton>
                </div>

                <div className="usuarios-modal-body">
                    <FormControl fullWidth error={!!errors.defecto}>
                        <InputLabel id="calidad-form-defecto-label">Tipo de defecto *</InputLabel>
                        <Select
                            labelId="calidad-form-defecto-label"
                            value={values.defecto}
                            onChange={handleChange('defecto')}
                        >
                            <MenuItem value=""><em>Selecciona un defecto</em></MenuItem>
                            {catalogoDefectos.map((d) => (
                                <MenuItem key={d.codigo} value={d.codigo}>{d.label}</MenuItem>
                            ))}
                        </Select>
                        {errors.defecto && (
                            <span className="calidad-form-error">{errors.defecto}</span>
                        )}
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="calidad-form-causa-label">Causa raíz (opcional)</InputLabel>
                        <Select
                            labelId="calidad-form-causa-label"
                            value={values.causaRaiz}
                            onChange={handleChange('causaRaiz')}
                        >
                            <MenuItem value=""><em>No especificada</em></MenuItem>
                            {catalogoCausas.map((c) => (
                                <MenuItem key={c.codigo} value={c.codigo}>{c.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Cantidad *"
                        type="number"
                        value={values.cantidad}
                        onChange={handleChange('cantidad')}
                        fullWidth
                        inputProps={{ min: 1 }}
                        error={!!errors.cantidad}
                        helperText={errors.cantidad}
                    />

                    <TextField
                        label={Number(values.cantidad) >= 2 ? 'Número de Lote' : 'Número de Serie'}
                        value={values.loteoSerie}
                        onChange={handleChange('loteoSerie')}
                        fullWidth
                        inputProps={{ maxLength: 100 }}
                    />

                    <TextField
                        label="Observaciones (opcional)"
                        value={values.notas}
                        onChange={handleChange('notas')}
                        fullWidth
                        multiline
                        rows={2}
                        inputProps={{ maxLength: 200 }}
                        placeholder="Descripción adicional del defecto..."
                    />

                    {errorGuardado && (
                        <span className="calidad-form-error">{errorGuardado}</span>
                    )}
                </div>

                <div className="usuarios-modal-footer">
                    <Button
                        variant="outlined"
                        className="usuarios-modal-cancel"
                        onClick={cerrarForm}
                        disabled={guardando}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className="usuarios-modal-submit"
                        onClick={handleSubmit}
                        disabled={guardando}
                        startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                        {guardando ? 'Guardando...' : editTarget ? 'Guardar cambios' : 'Registrar'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
